import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Trophy, MapPin, Rocket, Loader2, Info, Navigation, QrCode, Star, ArrowRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import api from '../services/api';

const LOCATION_LEADERBOARD_THRESHOLD = 50; // Show leaderboard when 50+ users have check-ins

export const HomepageLeaderboard = () => {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
    getUserLocation();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await api.get('/gamification/homepage-leaderboard');
      if (response.success) {
        setLeaderboardData(response);
      }
    } catch (error) {
      console.error('Error loading homepage leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          await loadNearbyLocations(latitude, longitude);
          setLocationLoading(false);
        },
        (error) => {
          console.log('Geolocation not available:', error.message);
          setLocationLoading(false);
          // Load some featured locations as fallback
          loadFeaturedLocations();
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    } else {
      loadFeaturedLocations();
    }
  };

  const loadNearbyLocations = async (lat, lng) => {
    try {
      const response = await api.get(`/locations/nearby?lat=${lat}&lng=${lng}&limit=5&radius_miles=100`);
      if (response.success && response.locations?.length > 0) {
        setNearbyLocations(response.locations);
      } else {
        // If no nearby locations, load featured ones
        loadFeaturedLocations();
      }
    } catch (error) {
      console.error('Error loading nearby locations:', error);
      loadFeaturedLocations();
    }
  };

  const loadFeaturedLocations = async () => {
    // Load interesting locations from various regions as fallback
    try {
      // Use center of US as default coordinates with wide radius
      const response = await api.get('/locations/nearby?lat=39.8283&lng=-98.5795&limit=5&radius_miles=2000');
      if (response.success && response.locations?.length > 0) {
        // Mark these as featured (no distance shown)
        setNearbyLocations(response.locations.map(loc => ({
          ...loc,
          distance_miles: null // Don't show distance for featured
        })));
      }
    } catch (error) {
      console.error('Error loading featured locations:', error);
    }
  };

  const getRankEmoji = (rank) => {
    const emojis = ['🥇', '🥈', '🥉', '4', '5'];
    return emojis[rank - 1] || `${rank}`;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'state_park': '🏞️',
      'national_park': '🏔️',
      'landmark': '🏛️',
      'beach': '🏖️',
      'museum': '🖼️',
      'restaurant': '🍽️',
      'hotel': '🏨',
      'default': '📍'
    };
    return icons[category] || icons.default;
  };

  // Generate point breakdown tooltip content for traveling items
  const getTravelingBreakdown = (leader) => {
    const storiesCount = leader.stories_count || 0;
    const storiesPoints = storiesCount * 10;
    
    return (
      <div className="text-xs min-w-[180px]">
        <div className="font-semibold text-blue-600 border-b border-blue-200 pb-1 mb-2">
          Point Breakdown
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Stories added:</span>
            <span className="font-medium text-blue-600">{storiesCount} x 10 = {storiesPoints}</span>
          </div>
          {leader.unique_items > 0 && (
            <div className="flex justify-between items-center text-gray-500 text-[11px]">
              <span>Unique items:</span>
              <span>{leader.unique_items}</span>
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 pt-1.5 mt-2 font-bold flex justify-between text-blue-700">
          <span>Total:</span>
          <span>{leader.points} pts</span>
        </div>
      </div>
    );
  };

  // Generate point breakdown tooltip content for location check-ins
  const getLocationBreakdown = (leader) => {
    const checkinsCount = leader.checkins_count || leader.checkins || leader.stories_count || 0;
    // Estimate: assume mix of QR scans (15) and GPS (10), average ~12 per check-in
    const avgPointsPerCheckin = 12;
    const checkinsPoints = checkinsCount * avgPointsPerCheckin;
    
    return (
      <div className="text-xs min-w-[180px]">
        <div className="font-semibold text-emerald-600 border-b border-emerald-200 pb-1 mb-2">
          Point Breakdown
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Check-ins:</span>
            <span className="font-medium text-emerald-600">{checkinsCount}</span>
          </div>
          <div className="flex justify-between items-center text-gray-500 text-[10px]">
            <span>QR scan: 15 pts • GPS: 10 pts</span>
          </div>
          {leader.unique_locations > 0 && (
            <div className="flex justify-between items-center text-gray-500 text-[11px]">
              <span>Unique locations:</span>
              <span>{leader.unique_locations}</span>
            </div>
          )}
          {leader.locations_created > 0 && (
            <div className="flex justify-between items-center text-purple-600 text-[11px]">
              <span>Locations added:</span>
              <span>{leader.locations_created} x 25</span>
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 pt-1.5 mt-2 font-bold flex justify-between text-emerald-700">
          <span>Total:</span>
          <span>{leader.points} pts</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-xl p-3 shadow-md border border-purple-100">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-purple-600 mr-2" />
          <span className="text-gray-600 text-sm">Loading leaderboards...</span>
        </div>
      </div>
    );
  }

  if (!leaderboardData) {
    return null;
  }

  const travelingLeaders = leaderboardData.traveling_items?.leaders || [];
  const locationLeaders = leaderboardData.guestbook_locations?.leaders || [];
  const showLocationLeaderboard = locationLeaders.length >= LOCATION_LEADERBOARD_THRESHOLD;

  const LeaderRow = ({ leader, index, type }) => {
    const tooltipContent = type === 'traveling' 
      ? getTravelingBreakdown(leader) 
      : getLocationBreakdown(leader);

    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div className={`flex items-center justify-between py-1.5 px-2 rounded cursor-help transition-colors ${
            index === 0 ? 'bg-yellow-50 hover:bg-yellow-100' : 
            index === 1 ? 'bg-gray-50 hover:bg-gray-100' : 
            index === 2 ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'
          }`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm w-5 text-center">{getRankEmoji(index + 1)}</span>
              <span className="font-medium text-gray-800 text-xs truncate max-w-[80px]">{leader.name || 'Anonymous'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge className={`text-xs px-1.5 py-0 ${type === 'traveling' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {leader.points} pts
              </Badge>
              <Info className="w-3 h-3 text-gray-400 hover:text-gray-600" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="left" 
          align="center"
          className="bg-white shadow-xl border border-gray-200 p-3 rounded-lg z-[100]"
          sideOffset={8}
        >
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  };

  const EmptyState = ({ type }) => (
    <div className="text-center py-3 text-gray-400">
      {type === 'traveling' ? <Rocket className="w-5 h-5 mx-auto mb-1 opacity-50" /> : <MapPin className="w-5 h-5 mx-auto mb-1 opacity-50" />}
      <p className="text-xs">No activity yet</p>
    </div>
  );

  // Nearby Locations Component (shown when < 50 location check-in users)
  const NearbyLocationsPanel = () => (
    <div className="bg-white/60 rounded-lg p-2 border border-emerald-100">
      <div className="flex items-center justify-between mb-1.5 pb-1 border-b border-emerald-100">
        <div className="flex items-center gap-1">
          <Navigation className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-xs font-semibold text-emerald-700">
            {userLocation ? 'Nearby Locations' : 'Featured Locations'}
          </span>
        </div>
        {locationLoading && <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />}
      </div>
      
      {nearbyLocations.length > 0 ? (
        <div className="space-y-1">
          {nearbyLocations.slice(0, 4).map((loc, index) => (
            <div 
              key={loc.uid || index}
              onClick={() => navigate(`/item/${loc.uid}`)}
              className="flex items-center justify-between py-1 px-1.5 rounded cursor-pointer hover:bg-emerald-50 transition-colors"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm">{getCategoryIcon(loc.category)}</span>
                <span className="text-xs text-gray-700 truncate max-w-[90px]">{loc.name}</span>
              </div>
              {loc.distance_miles && (
                <span className="text-[10px] text-emerald-600 whitespace-nowrap">
                  {loc.distance_miles < 1 ? '<1' : loc.distance_miles} mi
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-2 text-gray-400">
          <MapPin className="w-4 h-4 mx-auto mb-1 opacity-50" />
          <p className="text-[10px]">Enable location to see nearby spots</p>
        </div>
      )}
      
      {/* How to Earn Points Guide - 2 Column Layout */}
      <div className="mt-2 pt-2 border-t border-emerald-100">
        <p className="text-[10px] font-semibold text-emerald-700 mb-1">How to Earn Points:</p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] text-gray-600">
          <div className="flex items-center gap-1">
            <QrCode className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" />
            <span className="truncate">QR scan</span>
            <Badge className="ml-auto text-[7px] px-1 py-0 bg-emerald-100 text-emerald-700">+15</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Navigation className="w-2.5 h-2.5 text-blue-500 flex-shrink-0" />
            <span className="truncate">GPS check-in</span>
            <Badge className="ml-auto text-[7px] px-1 py-0 bg-blue-100 text-blue-700">+10</Badge>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5 text-purple-500 flex-shrink-0" />
            <span className="truncate">Add location</span>
            <Badge className="ml-auto text-[7px] px-1 py-0 bg-purple-100 text-purple-700">+25</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-2.5 h-2.5 text-yellow-500 flex-shrink-0" />
            <span className="truncate">First check-in</span>
            <Badge className="ml-auto text-[7px] px-1 py-0 bg-yellow-100 text-yellow-700">+10</Badge>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={100}>
      <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-xl p-3 shadow-md border border-purple-100">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <h2 className="text-sm font-bold text-gray-800">Monthly Leaderboard (iWasHere.me)</h2>
          </div>
          <Badge variant="outline" className="text-xs px-1.5 py-0 bg-white">
            Last 30 Days
          </Badge>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-3">
          {/* Traveling Items Column - Always shows leaderboard */}
          <div className="bg-white/60 rounded-lg p-2 border border-blue-100">
            <div className="flex items-center gap-1 mb-1.5 pb-1 border-b border-blue-100">
              <Rocket className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-semibold text-blue-700">Traveling Items</span>
            </div>
            <div className="space-y-1">
              {travelingLeaders.length > 0 ? (
                travelingLeaders.slice(0, 5).map((leader, index) => (
                  <LeaderRow key={leader.email || index} leader={leader} index={index} type="traveling" />
                ))
              ) : (
                <EmptyState type="traveling" />
              )}
            </div>
          </div>

          {/* Location Column - Shows leaderboard OR nearby locations + guide */}
          {showLocationLeaderboard ? (
            <div className="bg-white/60 rounded-lg p-2 border border-emerald-100">
              <div className="flex items-center gap-1 mb-1.5 pb-1 border-b border-emerald-100">
                <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700">Location Check-ins</span>
              </div>
              <div className="space-y-1">
                {locationLeaders.slice(0, 5).map((leader, index) => (
                  <LeaderRow key={leader.email || index} leader={leader} index={index} type="locations" />
                ))}
              </div>
            </div>
          ) : (
            <NearbyLocationsPanel />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default HomepageLeaderboard;
